/**
 * Data transformation utilities for CdpReports
 * Pure functions for merging, grouping, sorting, and transforming data
 */
import { isProviderOverBase } from './utils';

/**
 * Merges objects in an array by combining objects with the same key (Practice + ProcDate + patientId + ProcCode)
 * and summing their prices.
 * @param {Array} array - Array of objects to merge
 * @returns {Array} Merged array of objects
 */
export const mergeObjects = (array) => {
    const merged = {};
    for (let obj of array) {
        let key = obj.Practice + obj.ProcDate + obj.patientId + obj.ProcCode;
        if (merged[key]) {
            const carriers = [...new Set([merged[key].CarrierName, obj.CarrierName].filter(Boolean))].join(', ');
            Object.assign(merged[key], obj, { price: merged[key].price + obj.price });
            if (carriers) merged[key].CarrierName = carriers;
        } else {
            merged[key] = Object.assign({}, obj);
        }
    }
    return Object.values(merged);
}

/**
 * Groups an array of objects by a specified property.
 * @param {Array} objectArray - Array of objects to group
 * @param {string} property - Property name to group by
 * @returns {Object} Object with keys as property values and values as arrays of objects
 */
export const groupBy = (objectArray, property) => {
    return objectArray.reduce(function (acc, obj) {
        let key = obj[property]
        if (!acc[key]) {
            acc[key] = []
        }
        acc[key].push(obj)
        return acc
    }, {})
}

/**
 * Sorts object keys alphabetically and returns a new ordered object.
 * @param {Object} unOrderedObject - Object to sort
 * @returns {Object} New object with sorted keys
 */
export const orderObject = (unOrderedObject) => {
    return Object.keys(unOrderedObject).sort().reduce((obj, key) => {
        obj[key] = unOrderedObject[key];
        return obj;
    }, {});
}

/**
 * Sorts practices array by worked days in descending order.
 * @param {Array} practicesArray - Array of practice identifiers
 * @param {Object} doctorData - Object containing practice data with worked days
 * @returns {Array} Sorted array of practices
 */
export const sortPracticesByWorkedDays = (practicesArray, doctorData) => {
    if (!practicesArray || !doctorData) {
        return practicesArray;
    }
    return [...practicesArray].sort((a, b) => {
        const workedDaysA = (doctorData[a]?.worked || 0);
        const workedDaysB = (doctorData[b]?.worked || 0);
        return workedDaysB - workedDaysA; // Descending order (highest first)
    });
}

/**
 * Calculates patient visits per day.
 * @param {number} patientVisits - Total number of patient visits
 * @param {number} workedDays - Number of days worked
 * @returns {number} Visits per day (rounded up)
 */
export const calculateVisitsPerDay = (patientVisits, workedDays) => {
    return workedDays ? Math.ceil(patientVisits / workedDays) : 0;
}

/**
 * Initializes totals object for report calculations.
 * @returns {Object} Object with all totals initialized to 0 or empty arrays
 */
export const initializeTotals = () => ({
    totalBillableCare: 0,
    totalNormalizedBillableCare: 0,
    totalPatientVisits: 0,
    totalCare: 0,
    totalWriteOffs: 0,
    totalAdjustments: 0,
    totalProductionPay: 0,
    totalFinancialNotes: 0,
    totalBillableNotes: 0,
    totalPayByAccount: 0,
    dentistAbbrArr: [],
    baseDentists: [],
    customContractTotal: 0,
    ByPracHynBillableCareTotal: 0,
    totalHynBillableCare: 0,
    totalHygienistPayContribution: 0,
    grandtotalBasePay: 0,
    lockCount: [],
});

/**
 * Updates totals object with row data.
 * @param {Object} totals - Totals object to update
 * @param {Object} row - Row data containing provider information
 * @returns {void} Mutates totals object
 */
export const updateTotals = (totals, row) => {
    totals.totalPatientVisits += row.patientVisits || 0;
    totals.totalWriteOffs += row.writeOffPrice || 0;
    totals.totalAdjustments += row.adjustmentPrice || 0;
    totals.totalCare += row.price || 0;
    totals.totalBillableCare += parseFloat(row.bCare || 0);
    totals.totalNormalizedBillableCare += parseFloat(row.normalizedBillableCare || 0);
    totals.totalProductionPay += parseFloat(row.totalPay || 0);
    totals.totalPayByAccount += parseFloat(row.accountProcBasedPay || 0);
    totals.totalFinancialNotes += parseFloat(row.financialNotes || 0);
    totals.totalBillableNotes += parseFloat(row.billableNotes || 0);
    totals.grandtotalBasePay += parseFloat(row.displayBasePay || 0);
    totals.lockCount.push(row.lockStatus);

    if (row.customContract) {
        totals.customContractTotal++;
    }

    if (row.ABBR && row.contractType !== 'UKG HYN' && row.contractType !== 'TMPDOC' && row.contractType !== 'TMPHYN') {
        totals.dentistAbbrArr.push(row.ABBR.toLocaleLowerCase());
        if (isProviderOverBase(row, false)) {
            totals.baseDentists.push(row.ABBR.toLocaleLowerCase());
        }
    }
};

// Negative-only practices (e.g. write-offs/adjustments exceeding production)
// are real data too — only treat a metric as absent when it's exactly zero.
const hasValue = (val) => Number(val || 0) !== 0;

/**
 * Whether a single practice entry (row.data[practice]) has any meaningful
 * value in at least one of its reportable columns.
 * @param {Object} practiceObj - A practice entry from item.data[practiceKey]
 * @returns {boolean}
 */
export const practiceHasData = (practiceObj) => {
    const p = practiceObj || {};
    const pMetrics = [
        p.procBasedPay || p.practiceTotalPay || p.totalPay,
        p.financialNotes,
        p.price || p.practicePayBasedOnDailyRate,
        p.patientVisits,
        p.practiceBcare || p.bCare,
        p.worked,
        p.scheduled
    ];
    return pMetrics.some(hasValue);
};

/**
 * Filters data by removing items without meaningful values.
 * @param {Array} data - Array of data to prune
 * @returns {Array} Filtered array
 */
export const pruneData = (data) => {
    return (data || []).filter(item => {
        if (!item) return false;

        const dataObj = item.data || {};
        const practiceKeys = Object.keys(dataObj).filter(k => k !== 'reportData');
        if (!(item.doctorPracticesArray?.length > 0 || practiceKeys.length > 0)) return false;

        const topLevelMetrics = [
            item.bCare, item.price, item.procBasedPay || item.totalPay,
            item.financialNotes, item.firstDeposit, item.secondDeposit
        ];
        if (topLevelMetrics.some(hasValue)) return true;

        return practiceKeys.some(pk => practiceHasData(dataObj[pk]));
    });
}

/**
 * Returns column options configuration for the table.
 * @returns {Array} Array of column option objects
 */
export const getColumnOptions = () => {
    const obj = {
        'selectAll': 'Select Checkbox',
        'lockStatus': 'Lock Status',
        'provider': 'Provider',
        'ABBR': 'ABBR',
        'practice': 'Practice',
        'contractType': 'Contract Type',
        'workTimeType': 'Work Time Type',
        'workedDays': 'Worked Days',
        'scheduledDays': 'Scheduled Days',
        'RTOApproved': 'RTO - Approved',
        'FTOApproved': 'FTO - Approved',
        'patientVisits': 'Patient Visits',
        'bCPV': 'bCPV',
        'patientVisitsPerDay': 'PT Visits/Day',
        'rampStartDate': 'Ramp Start Date',
        'rampEndDate': 'Ramp End Date',
        'procedureDate': 'Procedure Date',
        'startEnd': 'Start - End',
        'care': 'Care',
        'writeOffs': 'Write Offs',
        'adjustments': 'Adjustments',
        'careFraction': 'Care Fraction',
        'bCare': 'bCare',
        'totalBasePay': 'Total Base Pay',
        'financialNotes': 'Financial Note',
        'totalPay': 'Total Pay',
        'normalizedPercentageOfBillableCare': '% of Billable'
    }
    return Object.keys(obj).map((key) => {
        return { key, text: obj[key], value: key }
    })
}
